import { Fragment, useEffect, useId, useMemo, type CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import KeyboardIcon from "@mui/icons-material/Keyboard";
import PublicIcon from "@mui/icons-material/Public";
import TextFieldsIcon from "@mui/icons-material/TextFields";
import WidgetsIcon from "@mui/icons-material/Widgets";

import {
  selectIsShortcutsOverlayOpen,
  setShortcutsOverlayOpen,
} from "../../../app/slices/OnboardingSlice";
import { isEditableElement } from "../../../hooks/UseShortcutsHook";
import PiModal from "../../../components/PiModal";
import { useOnboardingStrategy } from "../OnboardingHost";
import type { ShortcutGroup } from "../onboardingTypes";
import "./ShortcutsOverlay.css";

/**
 * The keyboard cheat sheet, opened with `?`.
 *
 * PiModal is used rather than PiDialog because it is the one modal in the
 * codebase that handles Escape and backdrop clicks correctly — PiDialog never
 * passes onClose, and MUI routes both through it.
 *
 * Presentation notes, since the layout is doing more than it looks:
 *  - Each group is a card on --pi-surface, sitting on the modal's
 *    --pi-background panel. That one step of contrast is what stops the sheet
 *    reading as a flat dump of rows.
 *  - Label left, keys right. Descriptions vary in length, key combos do not,
 *    so right-aligning the caps gives a tidy column to scan down.
 *  - Groups are packed into two balanced columns so the whole sheet is visible
 *    without scrolling at the usual editor size.
 */

/** Exported for testing: should this key event open the overlay? */
export function isShortcutsOverlayTrigger(event: KeyboardEvent): boolean {
  if (event.ctrlKey || event.metaKey || event.altKey) return false;
  // Guard exactly as every other shortcut does, so it never fires mid-typing.
  if (isEditableElement(event.target)) return false;
  return event.key === "?";
}

/**
 * Purely decorative, and keyed by the group's i18n key so it stays out of
 * shortcutGroups.ts — that file is content. A group with no entry here falls
 * back to the generic keyboard glyph rather than rendering a hole.
 */
const GROUP_ICONS: Readonly<Record<string, typeof KeyboardIcon>> = {
  "onboarding.shortcuts.group.global": PublicIcon,
  "onboarding.shortcuts.group.interactions": WidgetsIcon,
  "onboarding.shortcuts.group.sequence": AccountTreeIcon,
  "onboarding.shortcuts.group.text": TextFieldsIcon,
};

/**
 * Greedy balance by rendered height rather than group order, so the two
 * columns end up roughly level instead of one running far past the other.
 * A footnote is worth about two rows.
 *
 * Exported for testing.
 */
export function splitIntoColumns(
  groups: readonly ShortcutGroup[],
  columnCount = 2
): ShortcutGroup[][] {
  const columns: ShortcutGroup[][] = Array.from({ length: columnCount }, () => []);
  const weights: number[] = new Array(columnCount).fill(0);

  groups.forEach((group) => {
    let target = 0;
    for (let i = 1; i < columnCount; i++) {
      if (weights[i] < weights[target]) target = i;
    }
    columns[target].push(group);
    weights[target] +=
      group.entries.length + (group.footnoteKeys?.length ?? 0) * 2;
  });

  return columns.filter((column) => column.length > 0);
}

function ShortcutRow({ entry }: { entry: ShortcutGroup["entries"][number] }) {
  const { t } = useTranslation();

  return (
    <div className="flex items-center gap-3 px-2 py-[5px]">
      <span className="text-[12.5px] leading-snug text-creator-text">
        {t(entry.labelKey)}
      </span>
      <span className="ml-auto flex flex-none items-center gap-1">
        {entry.keys.map((key, index) => (
          <Fragment key={key}>
            {index > 0 && <span className="shortcuts-overlay__plus">+</span>}
            <kbd className="shortcuts-overlay__key">{key}</kbd>
          </Fragment>
        ))}
      </span>
    </div>
  );
}

/**
 * Caveats, not entries — so they get an icon, a tinted well and their own
 * indent, and deliberately do not line up with the shortcut rows above.
 */
function ShortcutFootnote({ footnoteKey }: { footnoteKey: string }) {
  const { t } = useTranslation();

  return (
    <p className="shortcuts-overlay__aside mt-2 flex gap-2 rounded-5 bg-[var(--pi-surface-hover)] px-2.5 py-2 text-[11px] leading-relaxed">
      <InfoOutlinedIcon
        aria-hidden="true"
        sx={{ fontSize: 14, flex: "none", mt: "1px" }}
      />
      <span>{t(footnoteKey)}</span>
    </p>
  );
}

function ShortcutGroupCard({
  group,
  index,
}: {
  group: ShortcutGroup;
  index: number;
}) {
  const { t } = useTranslation();
  const headingId = useId();
  const Icon = GROUP_ICONS[group.titleKey] ?? KeyboardIcon;

  return (
    // Named region as well as a heading, so a screen reader can jump group to
    // group rather than only heading to heading.
    <section
      aria-labelledby={headingId}
      className="shortcuts-overlay__group rounded-10 border border-creator-border bg-creator-surface p-3"
      style={{ "--shortcuts-delay": `${index * 45}ms` } as CSSProperties}
    >
      <h3
        id={headingId}
        className="mb-2 flex items-center gap-2 px-2 text-[12px] font-semibold text-creator-text"
      >
        <Icon
          aria-hidden="true"
          sx={{ fontSize: 15, color: "var(--pi-key-blue)" }}
        />
        {t(group.titleKey)}
      </h3>

      {group.entries.map((entry) => (
        <ShortcutRow key={entry.labelKey} entry={entry} />
      ))}

      {group.footnoteKeys?.map((key) => (
        <ShortcutFootnote key={key} footnoteKey={key} />
      ))}
    </section>
  );
}

export default function ShortcutsOverlay() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isOpen = useSelector(selectIsShortcutsOverlayOpen);
  const strategy = useOnboardingStrategy();

  const columns = useMemo(
    () => splitIntoColumns(strategy.shortcutGroups),
    [strategy.shortcutGroups]
  );

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isShortcutsOverlayTrigger(event)) return;
      event.preventDefault();
      dispatch(setShortcutsOverlayOpen(true));
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [dispatch]);

  if (!isOpen) return null;

  const close = () => dispatch(setShortcutsOverlayOpen(false));

  return (
    <PiModal
      isOpen={isOpen}
      onClose={close}
      title={String(t("onboarding.shortcuts.title"))}
      width="min(780px, 94vw)"
      height="auto"
    >
      <div
        data-testid="shortcuts-overlay"
        className="flex flex-col text-creator-text"
      >
        <div className="shortcuts-overlay__body playit-scrollbar overflow-y-auto pr-1">
          <div className="shortcuts-overlay__columns">
            {columns.map((column, columnIndex) => (
              <div key={columnIndex} className="flex flex-col gap-[14px]">
                {column.map((group, rowIndex) => (
                  <ShortcutGroupCard
                    key={group.titleKey}
                    group={group}
                    // Stagger across rows, not down columns, so the sheet
                    // assembles as one diagonal wave rather than two races.
                    index={rowIndex * columns.length + columnIndex}
                  />
                ))}
              </div>
            ))}
          </div>
        </div>

        {/* Border sits on the wrapper, not the text — the aside class dims
            with opacity, which would take the hairline down with it. */}
        <div className="mt-3 border-t border-creator-border pt-3">
          <p className="shortcuts-overlay__aside text-right text-[11px]">
            {t("onboarding.shortcuts.dismiss")}
          </p>
        </div>
      </div>
    </PiModal>
  );
}
